import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { publicApi } from '../utils/api';
import {
  Globe, Loader2, ExternalLink, RefreshCw, Newspaper,
  TrendingUp, Building2, BarChart3
} from 'lucide-react';

const CATEGORY_STYLES = {
  Business: { icon: Building2, color: '#00E5FF', bg: 'bg-[#00E5FF]/10' },
  Economy: { icon: TrendingUp, color: '#00E676', bg: 'bg-[#00E676]/10' },
  Markets: { icon: BarChart3, color: '#FFD740', bg: 'bg-[#FFD740]/10' },
};

const DEFAULT_STYLE = { icon: Globe, color: '#A0B4CC', bg: 'bg-white/5' };

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '';
  }
}

export function NewsWidget({ maxItems = 6, compact = false }) {
  const [articles, setArticles] = useState([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.get('/api/news');
      setArticles(data.articles || []);
      setLastRefreshedAt(data.last_refreshed_at || null);
    } catch (e) {
      console.error('News fetch failed:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  const categories = ['All', ...new Set(articles.map(a => a.category).filter(Boolean))];
  const filtered = filter === 'All' ? articles : articles.filter(a => a.category === filter);
  const displayed = filtered.slice(0, maxItems);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="text-center py-10">
        <Newspaper className="w-10 h-10 text-[#5C7599]/30 mx-auto mb-2" />
        <p className="text-[#5C7599] text-sm">No news available right now</p>
      </div>
    );
  }

  return (
    <div data-testid="news-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-outfit text-lg font-semibold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#00E5FF]" />
            Global News
          </h2>
          {lastRefreshedAt && (
            <p className="text-[#5C7599] text-[10px] mt-0.5" data-testid="news-last-refreshed">
              Updated {formatTimeAgo(lastRefreshedAt)}
            </p>
          )}
        </div>
        <button onClick={fetchNews} className="text-[#5C7599] hover:text-[#00E5FF] transition-colors" data-testid="refresh-news-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Category Filter */}
      {!compact && categories.length > 2 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                filter === cat
                  ? 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30'
                  : 'bg-white/5 text-[#A0B4CC] border border-white/10 hover:bg-white/10'
              }`}
              data-testid={`news-filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Articles */}
      <div className={compact ? 'space-y-2' : 'grid md:grid-cols-2 gap-3'}>
        {displayed.map((article, idx) => {
          const catStyle = CATEGORY_STYLES[article.category] || DEFAULT_STYLE;
          const CatIcon = catStyle.icon;

          if (compact) {
            return (
              <a
                key={`compact-${article.link}-${idx}`}
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 rounded-lg bg-[#041A3B]/50 border border-white/5 hover:border-[#00E5FF]/20 transition-all group"
                data-testid={`news-article-${idx}`}
              >
                <div className={`w-8 h-8 rounded-lg ${catStyle.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <CatIcon className="w-4 h-4" style={{ color: catStyle.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium line-clamp-2 group-hover:text-[#00E5FF] transition-colors">
                    {article.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[#5C7599] text-[10px]">{article.source}</span>
                    <span className="text-[#5C7599] text-[10px]">{formatTimeAgo(article.published)}</span>
                  </div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-[#5C7599] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
              </a>
            );
          }

          return (
            <a
              key={`card-${article.link}-${idx}`}
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
              data-testid={`news-article-${idx}`}
            >
              <Card className="bg-[#041A3B] border-white/5 hover:border-[#00E5FF]/20 transition-all h-full">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl ${catStyle.bg} flex items-center justify-center flex-shrink-0`}>
                      <CatIcon className="w-5 h-5" style={{ color: catStyle.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Badge className={`text-[9px] border-0 ${catStyle.bg}`} style={{ color: catStyle.color }}>
                          {article.category}
                        </Badge>
                        <span className="text-[#5C7599] text-[10px]">{formatTimeAgo(article.published)}</span>
                      </div>
                      <h3 className="text-white text-sm font-medium line-clamp-2 group-hover:text-[#00E5FF] transition-colors mb-1.5">
                        {article.title}
                      </h3>
                      {article.description && (
                        <p className="text-[#5C7599] text-xs line-clamp-2 mb-2">{article.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-[#5C7599] text-[10px] font-medium">{article.source}</span>
                        <ExternalLink className="w-3 h-3 text-[#5C7599] group-hover:text-[#00E5FF] transition-colors" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          );
        })}
      </div>
    </div>
  );
}
